import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageLoader, PageHeader, money } from "@/components/shared";
import { StarRating } from "@/components/StarRating";
import { ResaleSection } from "@/components/ResaleSection";
import { PlacesManager } from "@/components/PlacesManager";
import { TourPhotos } from "@/components/TourPhotos";
import { TourChecklist } from "@/components/TourChecklist";
import { NearbySection } from "@/components/NearbySection";
import { WishMatchBadge } from "@/components/WishMatch";
import { Disclaimer } from "@/components/Disclaimer";
import { CalendarDays, ExternalLink, Home as HomeIcon, TrendingUp, MapPin, Scale, Ruler, Navigation, GraduationCap, ShieldAlert, Waves, Check, X, Info, Star, ListChecks, Calculator as CalcIcon } from "lucide-react";

const mapsDir = (origin, dest) => `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}`;

const num = (s) => { const n = parseFloat(String(s ?? "").replace(/[^0-9.]/g, "")); return isNaN(n) ? null : n; };

function confidence(scores, criteria) {
  if (!criteria.length) return null;
  const avg = criteria.reduce((a, c) => a + (scores?.[c] ?? 0), 0) / criteria.length;
  if (avg >= 4) return { label: "High alignment", cls: "bg-status-complete/15 text-status-complete" };
  if (avg >= 2.5) return { label: "Moderate match", cls: "bg-status-warning/15 text-status-warning" };
  return { label: "Needs caution", cls: "bg-destructive/15 text-destructive" };
}

const VERDICTS = [
  { key: "love", label: "Love it", cls: "bg-status-complete text-white hover:bg-status-complete/90" },
  { key: "maybe", label: "Maybe", cls: "bg-status-warning text-white hover:bg-status-warning/90" },
  { key: "pass", label: "Pass", cls: "bg-destructive text-white hover:bg-destructive/90" },
];

export default function TourSchedule() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const myId = user?.id;
  const toursQ = useQuery({ queryKey: ["tours"], queryFn: async () => (await api.get("/buyer/tours")).data });
  const wsQ = useQuery({ queryKey: ["workspace"], queryFn: async () => (await api.get("/buyer/workspace")).data });

  const saveFeedback = useMutation({
    mutationFn: async ({ id, patch }) => (await api.put(`/buyer/tours/${id}/feedback`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tours"] }),
  });
  const toCompare = useMutation({
    mutationFn: async (id) => (await api.post(`/buyer/tours/${id}/to-compare`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["homes"] }); qc.invalidateQueries({ queryKey: ["workspace"] }); toast.success("On your Compare board — it stays in sync with this tour"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (toursQ.isLoading || wsQ.isLoading) return <PageLoader />;
  const tours = toursQ.data || [];
  const profile = wsQ.data?.client?.profile || {};
  const destinations = wsQ.data?.client?.destinations || [];
  const criteria = [...(profile.must_haves || []), ...(profile.nice_to_haves || [])];

  return (
    <div>
      <PageHeader title="Tour schedule" subtitle="Homes we're planning to tour together. Score each one against your must-haves, jot notes during the visit, and review comps and price-per-sqft. Comps are estimates for discussion only." />

      <div className="mb-6">
        <PlacesManager
          destinations={destinations}
          addFn={(d) => api.post("/buyer/destinations", d)}
          removeFn={(did) => api.delete(`/buyer/destinations/${did}`)}
          onChange={() => qc.invalidateQueries({ queryKey: ["workspace"] })}
          heading="Places that matter to me"
          subtitle="Add your work, your kids' school, the gym — then get one-tap directions from each home below. Maps shows you the live drive time."
        />
      </div>

      {tours.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center">
          <CalendarDays className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-heading text-lg font-semibold">No tours scheduled yet</p>
          <p className="text-muted-foreground text-sm mt-1">Your agent will add the homes you're going to tour here.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {tours.map((t, i) => {
            const conf = confidence(t.my_feedback?.criteria, criteria);
            const compPrices = (t.comps || []).map((c) => c.sold_price).filter((x) => x != null);
            const compAvg = compPrices.length ? Math.round(compPrices.reduce((a, b) => a + b, 0) / compPrices.length) : null;
            let gap = null;
            if (compAvg && t.price != null) {
              const diff = t.price - compAvg;
              const pct = Math.round((Math.abs(diff) / compAvg) * 100);
              gap = diff === 0 ? { text: "At comp average", cls: "text-muted-foreground" }
                : diff < 0 ? { text: `${money(Math.abs(diff))} (${pct}%) below comp avg`, cls: "text-status-complete" }
                  : { text: `${money(Math.abs(diff))} (${pct}%) above comp avg`, cls: "text-status-warning" };
            }
            const tSqft = num(t.sqft);
            const tPPSF = (t.price != null && tSqft) ? Math.round(t.price / tSqft) : null;
            const compPPSFvals = (t.comps || []).map((c) => { const s = num(c.sqft); return (c.sold_price != null && s) ? c.sold_price / s : null; }).filter((x) => x != null);
            const compPPSF = compPPSFvals.length ? Math.round(compPPSFvals.reduce((a, b) => a + b, 0) / compPPSFvals.length) : null;

            const fb = t.my_feedback || {};

            return (
              <motion.div key={t.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Card className="rounded-2xl overflow-hidden" data-testid={`tour-card-${i}`}>
                  <div className="grid md:grid-cols-[220px_1fr]">
                    <div className="h-44 md:h-full bg-secondary relative">
                      {t.image_url ? <img src={t.image_url} alt={t.title} className="w-full h-full object-cover" /> :
                        <div className="w-full h-full flex items-center justify-center"><HomeIcon className="h-10 w-10 text-muted-foreground/40" /></div>}
                      {t.tour_date && <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground border-0 gap-1"><CalendarDays className="h-3 w-3" /> {t.tour_date}</Badge>}
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading text-lg font-semibold">{t.title}</h3>
                            {t.ready_for_tour && <Badge className="border-0 bg-status-complete/15 text-status-complete" data-testid={`ready-badge-${i}`}>Ready for Tour</Badge>}
                          </div>
                          {t.address && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t.address}</p>}
                        </div>
                        <div className="text-right">
                          {t.price != null && <p className="font-semibold text-primary text-lg">{money(t.price)}</p>}
                          {conf && <Badge className={`border-0 mt-1 ${conf.cls}`}>{conf.label}</Badge>}
                          <div className="mt-1"><WishMatchBadge tour={t} profile={profile} testId={`tour-wish-score-${i}`} /></div>
                        </div>
                      </div>

                      {gap && (
                        <div className="mt-3 flex items-center gap-2 text-sm" data-testid={`tour-gap-${i}`}>
                          <Scale className="h-4 w-4 text-muted-foreground" />
                          <span>Asking price is <span className={`font-semibold ${gap.cls}`}>{gap.text}</span> <span className="text-muted-foreground">(avg of {compPrices.length} comp{compPrices.length > 1 ? "s" : ""}: {money(compAvg)})</span></span>
                        </div>
                      )}
                      {tPPSF && (
                        <div className="mt-1.5 flex items-center gap-2 text-sm" data-testid={`tour-ppsf-${i}`}>
                          <Ruler className="h-4 w-4 text-muted-foreground" />
                          <span><span className="font-semibold">${tPPSF.toLocaleString()}/sqft</span>{compPPSF ? <span className="text-muted-foreground"> · comps avg ${compPPSF.toLocaleString()}/sqft</span> : null}</span>
                        </div>
                      )}

                      {t.address && (
                        <div className="mt-3" data-testid={`tour-research-${i}`}>
                          <div className="flex flex-wrap gap-2">
                            {profile.workplace ? (
                              <a href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(t.address)}&destination=${encodeURIComponent(profile.workplace)}`} target="_blank" rel="noreferrer" data-testid={`tour-commute-${i}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors"><Navigation className="h-3 w-3" /> Commute to work</a>
                            ) : (
                              <a href="/app/onboarding" className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors"><Navigation className="h-3 w-3" /> Add your work address</a>
                            )}
                            <a href={`https://www.greatschools.org/search/search.page?q=${encodeURIComponent(t.address)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-forest hover:text-white transition-colors"><GraduationCap className="h-3 w-3" /> Schools</a>
                            <a href={`https://www.google.com/search?q=${encodeURIComponent(t.address + " crime statistics public safety")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-forest hover:text-white transition-colors"><ShieldAlert className="h-3 w-3" /> Crime & safety</a>
                            <a href={`https://msc.fema.gov/portal/search?AddressQuery=${encodeURIComponent(t.address)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-forest hover:text-white transition-colors"><Waves className="h-3 w-3" /> Flood map</a>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1.5">Third-party research you review yourself — we don't rate or interpret schools, crime, or neighborhoods.</p>
                        </div>
                      )}

                      {t.address && destinations.length > 0 && (
                        <div className="mt-3" data-testid={`tour-directions-${i}`}>
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5"><Navigation className="h-3.5 w-3.5" /> Drive times from this home</p>
                          <div className="flex flex-wrap gap-2">
                            {destinations.map((d) => (
                              <a key={d.id} href={mapsDir(t.address, d.address)} target="_blank" rel="noreferrer" data-testid={`tour-dir-${i}-${d.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors">
                                <Navigation className="h-3 w-3" /> {d.label}
                              </a>
                            ))}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1.5">Opens Google Maps — you'll see the live drive time there. We don't estimate it for you.</p>
                        </div>
                      )}

                      {t.notes && <p className="text-sm text-muted-foreground mt-2">{t.notes}</p>}

                      <NearbySection nearby={t.nearby} testId={`tour-nearby-${i}`} />

                      {(t.criteria_meets?.length > 0 || t.criteria_not_meets?.length > 0) && (
                        <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid={`tour-criteria-${i}`}>
                          {t.criteria_meets?.length > 0 && (
                            <div className="p-3 rounded-xl bg-status-complete/10 border border-status-complete/20">
                              <p className="text-xs font-semibold text-status-complete flex items-center gap-1 mb-1.5"><Check className="h-3.5 w-3.5" /> Meets your criteria</p>
                              <ul className="space-y-0.5">{t.criteria_meets.map((m) => <li key={m} className="text-xs text-foreground/80">{m}</li>)}</ul>
                            </div>
                          )}
                          {t.criteria_not_meets?.length > 0 && (
                            <div className="p-3 rounded-xl bg-status-warning/10 border border-status-warning/20">
                              <p className="text-xs font-semibold text-status-warning flex items-center gap-1 mb-1.5"><X className="h-3.5 w-3.5" /> Doesn't meet</p>
                              <ul className="space-y-0.5">{t.criteria_not_meets.map((m) => <li key={m} className="text-xs text-foreground/80">{m}</li>)}</ul>
                            </div>
                          )}
                        </div>
                      )}

                      {t.offer_prep ? (
                        <div className="mt-3 p-3 rounded-xl bg-primary/5 border border-primary/20" data-testid={`buyer-offer-prep-${i}`}>
                          <p className="text-sm font-semibold text-primary flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> What I'm still getting for you</p>
                          {(t.prep_checklist || []).filter((it) => !it.done).length > 0 ? (
                            <ul className="mt-2 space-y-1">
                              {(t.prep_checklist || []).filter((it) => !it.done).map((it) => (
                                <li key={it.id} className="text-sm text-foreground/80 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0" /> {it.label}</li>
                              ))}
                            </ul>
                          ) : <p className="text-sm text-muted-foreground mt-1">Everything's gathered — your agent will be in touch.</p>}
                          <p className="text-[11px] text-muted-foreground mt-2">Items your agent is gathering before an offer. Nothing here is a guarantee.</p>
                        </div>
                      ) : (
                        <div className="mt-3 p-3 rounded-xl bg-secondary" data-testid={`buyer-offer-cta-${i}`}>
                          <p className="text-sm">Interested in an offer? Let Marlene know, and she'll request everything needed.</p>
                        </div>
                      )}

                      <ResaleSection resale={t.resale} showEmpty />

                      <TourPhotos tour={t} />

                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {t.link && <a href={t.link} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline"><ExternalLink className="h-3 w-3" /> View listing</a>}
                        {t.price != null && (
                          <Button size="sm" variant="outline" className="ml-auto rounded-full gap-1 h-8" onClick={() => navigate(`/app/calculator?price=${t.price}`)} data-testid={`tour-estimate-payment-${i}`}>
                            <CalcIcon className="h-3.5 w-3.5" /> Estimate payment
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className={`rounded-full gap-1 h-8 ${t.price == null ? "ml-auto" : ""}`} onClick={() => toCompare.mutate(t.id)} disabled={toCompare.isPending} data-testid={`tour-to-compare-${i}`}>
                          <Scale className="h-3.5 w-3.5" /> Move to shortlist
                        </Button>
                      </div>

                      {t.estimated_value != null && (
                        <div className="mt-4 p-3 rounded-xl bg-forest/5 border border-forest/20">
                          <p className="text-xs font-semibold text-forest flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Agent's rough estimate of value</p>
                          <p className="font-heading text-xl font-bold mt-1">{money(t.estimated_value)}</p>
                          {t.estimate_note && <p className="text-xs text-muted-foreground mt-1">{t.estimate_note}</p>}
                          <p className="text-[11px] text-muted-foreground mt-2">An estimate for discussion only — not an appraisal or a guarantee of value or resale.</p>
                        </div>
                      )}

                      <Accordion type="single" collapsible className="mt-3">
                        {t.details && Object.keys(t.details).length > 0 && (
                          <AccordionItem value="details" className="border-0">
                            <AccordionTrigger className="text-sm py-2" data-testid={`tour-details-toggle-${i}`}>
                              <span className="flex items-center gap-2"><Info className="h-4 w-4 text-primary" /> Home details</span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                                {Object.entries(t.details).map(([k, v]) => (
                                  <div key={k} className="flex justify-between gap-3 border-b border-border/50 py-1">
                                    <dt className="text-muted-foreground">{k}</dt>
                                    <dd className="text-right font-medium">{String(v)}</dd>
                                  </div>
                                ))}
                              </dl>
                              {t.source_note && <p className="text-[11px] text-muted-foreground mt-3 italic">{t.source_note}</p>}
                            </AccordionContent>
                          </AccordionItem>
                        )}
                        <AccordionItem value="take" className="border-0">
                          <AccordionTrigger className="text-sm py-2" data-testid={`tour-take-toggle-${i}`}>
                            <span className="flex items-center gap-2"><Star className="h-4 w-4 text-amber-400" /> Your take on this home</span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-5">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Overall rating</p>
                                <StarRating value={fb.overall || 0} testid={`tour-overall-${i}`}
                                  onChange={(v) => saveFeedback.mutate({ id: t.id, patch: { overall: v } })} />
                              </div>
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Your verdict</p>
                                <div className="flex flex-wrap gap-2">
                                  {VERDICTS.map((vd) => (
                                    <Button key={vd.key} type="button" size="sm" variant={fb.verdict === vd.key ? "default" : "outline"}
                                      className={`rounded-full h-8 ${fb.verdict === vd.key ? vd.cls : ""}`}
                                      onClick={() => saveFeedback.mutate({ id: t.id, patch: { verdict: fb.verdict === vd.key ? null : vd.key } })}
                                      data-testid={`tour-verdict-${vd.key}-${i}`}>{vd.label}</Button>
                                  ))}
                                </div>
                              </div>
                              {criteria.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Rate your criteria</p>
                                  <div className="space-y-2">
                                    {criteria.slice(0, 10).map((c, ci) => (
                                      <div key={c} className="flex items-center justify-between gap-3">
                                        <span className="text-sm">{c}</span>
                                        <StarRating size="h-5 w-5" value={fb.criteria?.[c] || 0} testid={`tour-crit-${i}-${ci}`}
                                          onChange={(v) => saveFeedback.mutate({ id: t.id, patch: { criteria: { ...(fb.criteria || {}), [c]: v } } })} />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wider text-status-complete mb-1.5">Likes</p>
                                  <Textarea defaultValue={fb.likes || ""} placeholder="What you loved…" className="rounded-xl"
                                    onBlur={(e) => { if (e.target.value !== (fb.likes || "")) saveFeedback.mutate({ id: t.id, patch: { likes: e.target.value } }, { onSuccess: () => toast.success("Saved") }); }}
                                    data-testid={`tour-likes-${i}`} />
                                </div>
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wider text-status-warning mb-1.5">Dislikes</p>
                                  <Textarea defaultValue={fb.dislikes || ""} placeholder="What gave you pause…" className="rounded-xl"
                                    onBlur={(e) => { if (e.target.value !== (fb.dislikes || "")) saveFeedback.mutate({ id: t.id, patch: { dislikes: e.target.value } }, { onSuccess: () => toast.success("Saved") }); }}
                                    data-testid={`tour-dislikes-${i}`} />
                                </div>
                              </div>
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Private notes</p>
                                <Textarea defaultValue={fb.notes || ""} placeholder="Just for you…" className="rounded-xl"
                                  onBlur={(e) => { if (e.target.value !== (fb.notes || "")) saveFeedback.mutate({ id: t.id, patch: { notes: e.target.value } }, { onSuccess: () => toast.success("Saved") }); }}
                                  data-testid={`tour-notes-input-${i}`} />
                                <p className="text-[11px] text-muted-foreground mt-1">Private to you — your co-buyers and your agent can't see these.</p>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="checks" className="border-0">
                          <AccordionTrigger className="text-sm py-2" data-testid={`tour-checks-toggle-${i}`}>
                            <span className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-forest" /> Things to check on the tour</span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <TourChecklist tour={t} />
                          </AccordionContent>
                        </AccordionItem>

                        {t.comps?.length > 0 && (
                          <AccordionItem value="comps" className="border-0">
                            <AccordionTrigger className="text-sm py-2" data-testid={`tour-comps-toggle-${i}`}>Comparable sales ({t.comps.length})</AccordionTrigger>
                            <AccordionContent>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead><tr className="text-left text-muted-foreground border-b border-border">
                                    <th className="py-1.5 pr-3">Address</th><th className="py-1.5 pr-3">Sold</th><th className="py-1.5 pr-3">Date</th><th className="py-1.5 pr-3">Bd/Ba</th><th className="py-1.5">Sqft</th>
                                  </tr></thead>
                                  <tbody>
                                    {t.comps.map((c) => (
                                      <tr key={c.id} className="border-b border-border/50">
                                        <td className="py-1.5 pr-3">{c.address}{c.notes ? <span className="block text-muted-foreground">{c.notes}</span> : null}</td>
                                        <td className="py-1.5 pr-3 font-medium">{money(c.sold_price)}</td>
                                        <td className="py-1.5 pr-3">{c.sold_date || "—"}</td>
                                        <td className="py-1.5 pr-3">{c.beds || "—"}/{c.baths || "—"}</td>
                                        <td className="py-1.5">{c.sqft || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                      </Accordion>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <div className="mt-6">
        <Disclaimer>Scores and notes reflect only your own priorities. Comparable sales, price-per-sqft and estimated values are gathered by your agent for discussion and are estimates only — not an appraisal, a price recommendation, or a guarantee of future value or resale. Confirm value with a licensed appraiser.</Disclaimer>
      </div>
    </div>
  );
}
